import React from 'react';
import i18n from 'util/i18n';
import ValidationElement from 'components/Form/ValidationElement';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonScrollIntoView, CommonRequired
} from 'types/props';
import { ErrorCode } from 'redux/modules/application/reducers/error';
import {
  LegalInvestigationsClearanceHistoryItemModel,
  LegalInvestigationsInvestigatingAgencyModel,
} from 'forms/sections/legal/investigation/history/types';
import Field from 'components/Form/Field';
import NotApplicable from 'components/Form/NotApplicable';
import Textarea from 'components/Form/Textarea';
import { isPVQForm } from 'helpers/form';
import InvestigatingAgency from './InvestigatingAgency';

interface Props extends Partial<LegalInvestigationsClearanceHistoryItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  formType?: string
}

export default class ClearanceHistoryItem extends ValidationElement<Props> {
  update = (queue: Partial<LegalInvestigationsClearanceHistoryItemModel>) => {
    this.props.onUpdate({
      AgencyNotApplicable: this.props.AgencyNotApplicable,
      Agency: this.props.Agency,
      AgencyExplanation: this.props.AgencyExplanation,
      ...queue,
    });
  };

  updateAgencyNotApplicable = (
    values: LegalInvestigationsClearanceHistoryItemModel['AgencyNotApplicable']
  ) => {
    this.update({
      AgencyNotApplicable: values,
      Agency: {} as LegalInvestigationsClearanceHistoryItemModel['Agency'],
      AgencyExplanation: {} as LegalInvestigationsClearanceHistoryItemModel['AgencyExplanation'],
    });
  };

  updateAgency = (values: Partial<LegalInvestigationsClearanceHistoryItemModel>) => {
    this.update({
      Agency: values.Agency,
      AgencyExplanation: values.Agency?.value === 'Other' ? this.props.AgencyExplanation : {} as LegalInvestigationsClearanceHistoryItemModel['AgencyExplanation'],
    });
  };

  updateAgencyExplanation = (value: LegalInvestigationsClearanceHistoryItemModel['AgencyExplanation']) => {
    this.update({
      AgencyExplanation: value
    });
  };

  render() {
    const attribute = `ClearanceHistoryList.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div>
        <Field
          title={i18n.t('legal.pvq.investigations.clearancehistory.heading.agency')}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Agency`}
          exactAttribute
        >
          <InvestigatingAgency
            Agency={this.props.Agency}
            Explanation={this.props.AgencyExplanation}
            name="Agency"
            onUpdate={this.updateAgency}
            onError={this.props.onError}
            className="legal-investigations-history-agency"
            required={this.props.required && this.props.AgencyNotApplicable?.applicable}
            scrollIntoView={this.props.scrollIntoView}
            attribute={attribute}
            formType={this.props.formType}
          />
        </Field>

        {this.props.Agency?.value === 'Other'
          && (
            <Field
              title={i18n.t('legal.pvq.investigations.history.heading.agencyexplanation')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.AgencyExplanation`}
              exactAttribute
            >
              <Textarea
                {...this.props.AgencyExplanation}
                name="AgencyExplanation"
                className="legal-investigations-clearance-agency-explanation"
                onUpdate={this.updateAgencyExplanation}
                onError={this.props.onError}
                required={this.props.required}
                disabled={this.props.disabled}
                attribute={`${this.props.attribute}.AgencyExplanation`}
              />
            </Field>
          )}
      </div>
    ) : (
      <div>
        <Field
          title={i18n.t('legal.pvq.investigations.clearancehistory.heading.agency')}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Agency`}
          exactAttribute
        >
          <NotApplicable
            {...this.props.AgencyNotApplicable}
            name="AgencyNotApplicable"
            className="legal-investigations-clearancehistory-agency-notapplicable"
            onUpdate={this.updateAgencyNotApplicable}
            onError={this.props.onError}
            or={i18n.m('legal.pvq.investigations.history.para.or')}
            label={i18n.t('legal.pvq.investigations.history.label.idk')}
            required={this.props.required}
            attribute={`${attribute}.AgencyNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Agency?.value || this.props.AgencyExplanation?.value)}
          >
            <InvestigatingAgency
              {...{
                Agency: this.props.Agency,
                Explanation: this.props.AgencyExplanation,
              }}
              name="Agency"
              onUpdate={this.updateAgency}
              onError={this.props.onError}
              className="legal-investigations-history-agency"
              required={this.props.required && this.props.AgencyNotApplicable?.applicable}
              scrollIntoView={this.props.scrollIntoView}
              attribute={attribute}
              formType={this.props.formType}
            />
          </NotApplicable>
        </Field>

        {this.props.Agency?.value === 'Other'
          && (
            <Field
              title={i18n.t('legal.pvq.investigations.history.heading.agencyexplanation')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.AgencyExplanation`}
              exactAttribute
            >
              <Textarea
                {...this.props.AgencyExplanation}
                name="AgencyExplanation"
                className="legal-investigations-clearance-agency-explanation"
                onUpdate={this.updateAgencyExplanation}
                onError={this.props.onError}
                required={this.props.required}
                disabled={this.props.disabled}
                attribute={`${this.props.attribute}.AgencyExplanation`}
              />
            </Field>
          )}
      </div>
    );
  }
}

ClearanceHistoryItem.defaultProps = {
  required: false,
  onUpdate: () => { },
  onError: (value, arr: ErrorCode[]) => arr,
  AgencyNotApplicable: { applicable: true }
};
